import { useEffect } from 'react';
import type { LucideIcon } from 'lucide-react';
import { X } from 'lucide-react';
import { useSupportChatFabLayout, type SupportChatFabPanelMaxRem } from '@/stores/supportChatFabLayout.store';

export interface TrainerRightPanelHeaderProps {
  icon: LucideIcon;
  title: string;
  /** Sous-titre sous le titre (optionnel) */
  description?: string;
  onClose: () => void;
  closeDisabled?: boolean;
  titleId?: string;
  closeAriaLabel?: string;
  /**
   * Décale la bulle support pour qu’elle reste à gauche du panneau (`max-w-md` / `max-w-xl`).
   * Désactiver si le header n’est pas dans un overlay pleine hauteur à droite.
   */
  shiftSupportChatFab?: boolean;
  /** Aligné sur la classe du conteneur : `max-w-md` → 28rem, `max-w-xl` → 36rem. */
  supportFabPanelMaxWidth?: 'md' | 'xl';
}

/**
 * En-tête commun des panneaux latéraux droits du dashboard formateur
 * (dégradé primaire institution / app, icône, fermeture).
 */
export function TrainerRightPanelHeader({
  icon: Icon,
  title,
  description,
  onClose,
  closeDisabled = false,
  titleId,
  closeAriaLabel = 'Fermer',
  shiftSupportChatFab = true,
  supportFabPanelMaxWidth = 'md',
}: TrainerRightPanelHeaderProps) {
  const maxRem: SupportChatFabPanelMaxRem = supportFabPanelMaxWidth === 'xl' ? 36 : 28;

  useEffect(() => {
    if (!shiftSupportChatFab) return;
    const { pushPanel, popPanel } = useSupportChatFabLayout.getState();
    pushPanel(maxRem);
    return () => {
      popPanel();
    };
  }, [shiftSupportChatFab, maxRem]);

  return (
    <div
      className="relative shrink-0 overflow-hidden px-5 pb-5 pt-5 text-white sm:px-6 sm:pb-6 sm:pt-6"
      style={{
        background: `linear-gradient(155deg, var(--inst-player-primary, #0C1C38) 0%, color-mix(in srgb, var(--inst-player-primary, #0C1C38) 72%, #0f172a) 100%)`,
      }}
    >
      <div
        className="pointer-events-none absolute -end-10 -top-12 h-36 w-36 rounded-full bg-white/[0.07]"
        aria-hidden
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <span
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 shadow-inner ring-1 ring-white/25"
            aria-hidden
          >
            <Icon className="h-6 w-6 text-white" strokeWidth={2} />
          </span>
          <div className="min-w-0 pt-0.5">
            <h2 id={titleId} className="text-lg font-semibold leading-tight tracking-tight">
              {title}
            </h2>
            {description ? (
              <p className="mt-1.5 line-clamp-4 text-sm leading-snug text-white/88">{description}</p>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          onClick={() => !closeDisabled && onClose()}
          disabled={closeDisabled}
          className="shrink-0 rounded-xl p-2 text-white/90 transition hover:bg-white/15 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:opacity-50"
          aria-label={closeAriaLabel}
        >
          <X className="h-5 w-5" strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}
